import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { switchMap, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import MaterialListAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/materials/material-list/material-list.entity';
import MaterialListComponentAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/materials/material-list-component/material-list-component.entity';
import { MaterialListService } from 'app/modules/components/material/material-list/material-list.service';
import { mapMaterialListToBomModalData } from 'app/modules/components/material/material/utils/map-material-list-to-bom-modal-data';
import { BomModalComponent } from 'app/shared/components/bom-modal/bom-modal.component';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { DocumentGenerateService } from '../../document-generate.service';
import { DocumentGenerateConfigService } from '../../document-generate-config.service';

// Icono BOM por línea: resuelve la lista y componentes del material y abre el modal
@Component({
  selector: 'app-bom-cell',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  template: `
    @if (material?.isBOM) {
    <button
      type="button"
      class="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-indigo-100 p-1 text-indigo-700 transition-colors duration-200 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      [matTooltip]="
        material?.materialList?.name
          ? ('DOCUMENT_GENERATE.BOM_CELL.VIEW_COMPONENTS_OF' | t: { name: material.materialList.name })
          : ('DOCUMENT_GENERATE.BOM_CELL.VIEW_STRUCTURE' | t)
      "
      matTooltipPosition="above"
      (click)="openBom($event)"
    >
      <mat-icon class="icon-size-4" svgIcon="heroicons_outline:cube"></mat-icon>
    </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BomCellComponent {
  @Input() material: any;

  private destroy$ = new Subject<void>();

  constructor(
    private readonly dialog: MatDialog,
    private readonly materialListService: MaterialListService,
    private readonly service: DocumentGenerateService,
    private readonly handlerMessage: HandlerMessageService,
    private readonly dgConfig: DocumentGenerateConfigService,
    private readonly i18n: LanguageService,
  ) {}

  openBom(event: MouseEvent): void {
    event.stopPropagation();

    const listId =
      this.material?.materialList?._id?.toString() ??
      this.material?.BOMMongoId?.toString();

    if (!listId) {
      this.handlerMessage.warning({
        message: this.i18n.t('DOCUMENT_GENERATE.BOM_CELL.NO_LIST'),
      });
      return;
    }

    this.materialListService
      .findOne(listId)
      .pipe(
        switchMap((listResponse) => {
          const list = listResponse.data as MaterialListAttributes;
          if (!list?.code) {
            throw new Error(this.i18n.t('DOCUMENT_GENERATE.BOM_CELL.MISSING_CODE'));
          }
          return this.service
            .fetchMaterialListComponents(list.code)
            .pipe(map((componentsResponse) => ({ list, componentsResponse })));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ list, componentsResponse }) => {
          const componentRows = (componentsResponse?.data?.[0]?.data ?? []) as unknown as MaterialListComponentAttributes[];
          const data = mapMaterialListToBomModalData(list, componentRows);
          this.dialog.open(BomModalComponent, {
            data,
            ...this.dgConfig.dialogSize('lg'),
            autoFocus: true,
            restoreFocus: true,
          });
        },
        error: (err) => {
          this.handlerMessage.error({
            message: err?.error?.message ?? err?.message,
            alternativeMessage: this.i18n.t('DOCUMENT_GENERATE.BOM_CELL.LOAD_ERROR'),
            statusCode: err?.status,
          });
        },
      });
  }
}
